import type { MotionStyle, MotionValue } from "motion/react";
import { cubicBezier, motion, useTransform } from "motion/react";

/**
 * Obtains the transform styles for a letter based on its index and the scroll progress
 * @param scrollProgress The current scroll progress
 * @param index The index of the letter
 * @param durationPerLetter The duration of the animation per letter
 * @returns The styles for the letter
 */
const getStyles = (scrollProgress: MotionValue<number>, index: number, durationPerLetter: number) => {
  // Have each letter start animating one after another based on their index
  const start = index * durationPerLetter;
  const end = start + durationPerLetter;

  const ease = cubicBezier(0, 0.55, 0.45, 1); // easeOutCirc
  return {
    y: useTransform(scrollProgress, [start, end], [30, 0], { ease }),
    opacity: useTransform(scrollProgress, [start, end], [0, 1], { ease }),
  };
};

/**
 * Applies a pull-up animation to each letter of the given text based on scroll progress
 * @param text The text to animate
 * @param startingIndex Overrides the starting index of the text (for cross-component animations)
 * @param length The length of the text (for overriding the text length calculation. used for cross-component animations)
 * @param children Optional children to render instead of text (will treat the entire child as a single unit)
 * @param className Class styles
 * @param style Styles
 * @param scrollProgress The current scroll progress
 * @returns The text supplied with a pull-up animation applied to each letter
 */
export default function LettersPullUp({
  text,
  startingIndex,
  length,
  children,
  className,
  style,
  scrollProgress,
}: {
  text?: string;
  startingIndex?: number;
  length?: number;
  children?: React.ReactNode;
  className?: string;
  style?: MotionStyle;
  scrollProgress: MotionValue<number>;
}) {
  // Calculate the total duration of each letters' animation based on the length of the text (with some slight overlap)
  const durationPerLetter = 1 / (length ?? text?.length ?? 1); // Use the length of the text unless overridden. Defaults to 1 if no override nor text is provided (as we assume a child is provided in that case)

  return (
    <motion.div
      className={`flex overflow-hidden ${className ?? ""}`}
      style={{ ...style, ...getStyles(scrollProgress, startingIndex ?? 0, durationPerLetter) }}
    >
      {children
        ? children
        : text?.split("").map((letter, i) => {
            // Get the transform styles for the letter
            const styles = getStyles(scrollProgress, i + (startingIndex ?? 0), durationPerLetter);
            return (
              <motion.h2 key={i} className='shrink-0' style={styles}>
                {letter === " " ? "\u00A0" : letter}
              </motion.h2>
            );
          })}
    </motion.div>
  );
}
